/**
 * Deck text exporter (MTGA / Moxfield format)
 *
 * Exports decks as text with zone headers.
 * Supports MTGA style (set codes, simple headers) and Moxfield style (zone counts).
 */
import { DECK_ZONE_LABELS } from '@/lib/deckUtils';
import type { DeckZone } from '@/types/deck';

import type { ExportableDeckCard } from '../../decks/lib/deckImportExport';

// ============================================================================
// Types
// ============================================================================

export interface DeckExportOptions {
  includeSetInfo?: boolean;
  includeMaybeboard?: boolean;
  includeTokens?: boolean;
  format?: 'mtga' | 'moxfield';
}

// ============================================================================
// Export
// ============================================================================

/**
 * Export a deck to a text format.
 */
export function exportDeckList(cards: ExportableDeckCard[], deckName?: string, options: DeckExportOptions = {}): string {
  const { includeSetInfo = true, includeMaybeboard = false, includeTokens = false, format = 'mtga' } = options;

  const lines: string[] = [];

  // Add deck name if provided
  if (deckName && format === 'moxfield') {
    lines.push(`// ${deckName}`);
    lines.push('');
  }

  // Group cards by zone
  const zones: DeckZone[] = ['commander', 'companion', 'main', 'sideboard'];
  if (includeMaybeboard) zones.push('maybeboard');
  if (includeTokens) zones.push('tokens');

  // For MTGA format, merge maybeboard into sideboard since MTGA doesn't support maybeboard
  const getMergedZoneCards = (zone: DeckZone) => {
    if (format === 'mtga' && zone === 'sideboard' && includeMaybeboard) {
      // Combine sideboard and maybeboard cards
      return cards.filter((c) => c.zone === 'sideboard' || c.zone === 'maybeboard').sort((a, b) => a.card.name.localeCompare(b.card.name));
    }
    return cards.filter((c) => c.zone === zone).sort((a, b) => a.card.name.localeCompare(b.card.name));
  };

  for (const zone of zones) {
    // Skip maybeboard for MTGA (already merged into sideboard)
    if (format === 'mtga' && zone === 'maybeboard') continue;

    const zoneCards = getMergedZoneCards(zone);

    if (zoneCards.length === 0) continue;

    // Add zone header
    if (format === 'moxfield') {
      const totalQty = zoneCards.reduce((sum, c) => sum + c.quantity, 0);
      lines.push(`${DECK_ZONE_LABELS[zone]} (${totalQty})`);
    } else if (zone !== 'main') {
      // MTGA style: just add a blank line before sideboard, comment for others
      if (zone === 'sideboard') {
        lines.push('');
        lines.push('Sideboard');
      } else if (zone === 'commander') {
        lines.push('Commander');
      } else if (zone === 'companion') {
        lines.push('Companion');
      }
    }

    // Add cards
    for (const item of zoneCards) {
      const line = formatCardLine(item, includeSetInfo);
      lines.push(line);
    }

    if (format === 'moxfield') {
      lines.push('');
    }
  }

  return lines.join('\n').trim();
}

// ============================================================================
// Helpers
// ============================================================================

/** The minimum a card line needs; `ExportableDeckCard` and list entries both satisfy it. */
export interface ExportableCardLine {
  quantity: number;
  card: { name: string; set: string; collector_number: string };
}

/**
 * Format a single card line.
 * Shared by the text, MTGO and list exporters.
 */
export function formatCardLine(item: ExportableCardLine, includeSetInfo: boolean): string {
  const { quantity, card } = item;
  const name = card.name;

  if (includeSetInfo) {
    const setCode = card.set.toUpperCase();
    const collectorNumber = card.collector_number;
    return `${quantity} ${name} (${setCode}) ${collectorNumber}`;
  }

  return `${quantity} ${name}`;
}
